import React, { useEffect, useRef, useState } from 'react'
import { BoardBadge } from '../../components/lease/BoardBadge'
import { AppHeader, field } from '../ui'
import { attorneyApi, attorneyAuth } from '../api'

const barValid = (v) => /^\d{1,6}$/.test((v || '').trim()) && Number((v || '').trim()) > 0

// Where renters browse the attorney directory. In dev, point at the local
// adportal dev server (vite.adportal.config.js, port 5176) so the button is
// testable before the production subdomain is deployed.
const PUBLIC_DIRECTORY_URL = import.meta.env.DEV
  ? 'http://localhost:5176/adportal.html'
  : 'https://adportal.coloradoleasecheck.com/'

// Open the live public directory as the attorney, in a read-only preview. The
// directory is a separate origin, so the JWT can't cross via localStorage —
// hand it off once in the URL (the adportal bundle captures + strips it), with
// view=attorney (hides the renter contact form) and me=<id> (marks their card).
function openPublicDirectory(attorneyId) {
  const token = localStorage.getItem('attorney_token')
  const params = new URLSearchParams({ view: 'attorney' })
  if (token) params.set('token', token)
  if (attorneyId) params.set('me', String(attorneyId))
  window.open(`${PUBLIC_DIRECTORY_URL}?${params.toString()}`, '_blank', 'noopener')
}

function Card({ title, desc, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
      {title && <h2 className="text-xl font-bold tracking-tight text-slate-900">{title}</h2>}
      {desc && <p className="mt-1.5 text-sm text-slate-500">{desc}</p>}
      <div className={title ? 'mt-6' : ''}>{children}</div>
    </section>
  )
}

function StepTracker({ current }) {
  const steps = ['Account', 'Choose a plan', 'Complete profile', 'Go live']
  return (
    <ol className="mb-8 flex items-center gap-2 text-xs font-semibold sm:text-sm">
      {steps.map((label, i) => {
        const n = i + 1
        const done = n < current
        const active = n === current
        return (
          <React.Fragment key={label}>
            <li className="flex items-center gap-2">
              <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-full ${done ? 'bg-emerald-600 text-white' : active ? 'bg-white text-emerald-700 ring-2 ring-emerald-500' : 'bg-slate-200 text-slate-500'}`}>
                {done ? '✓' : n}
              </span>
              <span className={`hidden sm:inline ${done || active ? 'text-slate-900' : 'text-slate-400'}`}>{label}</span>
            </li>
            {i < steps.length - 1 && <li className="h-px flex-1 bg-slate-200" />}
          </React.Fragment>
        )
      })}
    </ol>
  )
}

// Module-level so it isn't re-created on every Portal render — a Shell defined
// inside Portal remounts the whole subtree each keystroke, which drops input
// focus after one character.
function Shell({ status, onSignOut, msg, step, children, width = 'max-w-2xl' }) {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <AppHeader status={status} onSignOut={onSignOut} />
      <main className={`mx-auto ${width} px-5 py-10`}>
        {step && <StepTracker current={step} />}
        {msg && <p className="mb-5 rounded-lg bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-800">{msg}</p>}
        {children}
      </main>
    </div>
  )
}

// Shared profile form (used in the Complete-profile step and the live dashboard).
function ProfileForm({ me, setMe, emphasizeBar, submitLabel, onMsg, onConfirmed }) {
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState('')
  // Inline result next to the submit button — the Shell banner renders at the
  // top of the page, off-screen when the form (bottom of the dashboard) is in
  // view, so a save looked like a no-op.
  const [note, setNote] = useState(null) // { text, ok }
  const fileRef = useRef(null)
  const setF = (k) => (e) => setMe({ ...me, [k]: e.target.value })
  const setSpecialties = (e) => setMe({ ...me, specialties: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })

  const save = async (e) => {
    e.preventDefault()
    setSaving(true); onMsg(''); setNote(null)
    try {
      const updated = await attorneyApi.updateProfile({
        firm: me.firm, email: me.email, phone: me.phone, bio: me.bio, website: me.website,
        specialties: me.specialties,
        years_experience: me.years_experience ? Number(me.years_experience) : null,
        bar_number: me.bar_number,
      })
      setMe(updated)
      if (onConfirmed) onConfirmed()
      else {
        const text = updated.status === 'active' ? 'Your profile is live!' : 'Saved.'
        onMsg(text)
        setNote({ text: text === 'Saved.' ? 'Saved ✓' : 'Saved ✓ — your profile is live', ok: true })
      }
    } catch (err) {
      const text = err.message || 'Could not save'
      onMsg(text)
      setNote({ text, ok: false })
    }
    setSaving(false)
  }

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    // Instant local preview so the photo is never accepted blindly.
    // ponytail: objectURL not revoked — one-off, page-scoped.
    setPreview(URL.createObjectURL(f))
    try { setMe(await attorneyApi.uploadPhoto(f)); onMsg('Photo updated.') }
    catch (err) { onMsg(err.message || 'Could not upload photo') }
  }

  const barShown = (me.bar_number || '').trim()
  const barBad = barShown && !barValid(barShown)
  const photoSrc = preview || me.photo_url

  return (
    <form className="space-y-5" onSubmit={save}>
      <div className="flex items-center gap-4">
        <span className="flex h-16 w-16 flex-none items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-lg font-bold text-white">
          {photoSrc ? <img src={photoSrc} alt="" className="h-full w-full object-cover" /> : (me.name || 'A').slice(0, 2).toUpperCase()}
        </span>
        <div>
          <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" />
          <button type="button" onClick={() => fileRef.current?.click()}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">Upload photo</button>
          <p className="mt-1 text-xs text-slate-400">JPG, PNG, or WebP.</p>
        </div>
      </div>

      {/* Bar number (go-live gate) + notification email, side by side */}
      <div className={emphasizeBar ? 'rounded-xl border border-amber-200 bg-amber-50 p-4' : ''}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-semibold text-slate-800">
              Colorado bar number <span className="text-red-500">*</span>
              <input className={field} value={me.bar_number || ''} onChange={setF('bar_number')} placeholder="e.g. 45879" inputMode="numeric" />
            </label>
            {barBad
              ? <p className="mt-1.5 text-xs font-medium text-red-600">Enter your numeric Colorado registration number (up to 6 digits).</p>
              : <p className="mt-1.5 text-xs text-slate-500">Required to publish your profile to renters. We verify the format before your profile goes live.</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-800">
              Email <span className="text-red-500">*</span>
              <input type="email" className={field} value={me.email || ''} onChange={setF('email')} placeholder="you@firm.com" />
            </label>
            <p className="mt-1.5 text-xs text-slate-500">Where we send referrals and your confirmation. Pre-filled from your login — edit if you prefer a different inbox.</p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-medium text-slate-700">Firm
          <input className={field} value={me.firm || ''} onChange={setF('firm')} /></label>
        <label className="block text-sm font-medium text-slate-700">Phone
          <input className={field} value={me.phone || ''} onChange={setF('phone')} /></label>
        <label className="block text-sm font-medium text-slate-700">Years of experience
          <input type="number" className={field} value={me.years_experience || ''} onChange={setF('years_experience')} /></label>
        <label className="block text-sm font-medium text-slate-700">Website
          <input className={field} value={me.website || ''} onChange={setF('website')} placeholder="https://" /></label>
      </div>
      <label className="block text-sm font-medium text-slate-700">Specialties <span className="font-normal text-slate-400">(comma-separated)</span>
        <input className={field} value={(me.specialties || []).join(', ')} onChange={setSpecialties} placeholder="Tenant rights, Security deposits" /></label>
      <label className="block text-sm font-medium text-slate-700">Bio
        <textarea className={field} rows={4} value={me.bio || ''} onChange={setF('bio')} placeholder="Tell renters about your practice and how you help." /></label>

      <div className="flex flex-wrap items-center gap-4">
        <button type="submit" disabled={saving}
          className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 py-3.5 text-base font-bold text-white shadow transition hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto sm:px-8">
          {saving ? 'Saving…' : submitLabel}
        </button>
        {note && (
          <span className={`text-sm font-semibold ${note.ok ? 'text-emerald-700' : 'text-red-600'}`}>
            {note.text}
          </span>
        )}
      </div>
    </form>
  )
}

export function Portal() {
  const [me, setMe] = useState(null)
  const [plans, setPlans] = useState({})
  const [seats, setSeats] = useState(null)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    attorneyApi.me().then(setMe).catch(() => {})
    attorneyApi.plans().then((d) => setPlans(d.plans)).catch(() => {})
    attorneyApi.boardSeats().then(setSeats).catch(() => {})
  }, [])

  const signOut = () => { attorneyAuth.logout(); window.location.assign('/login') }

  if (!me) return <div className="flex min-h-screen items-center justify-center bg-slate-50 font-sans text-slate-500">Loading…</div>

  const active = me.subscription_status === 'active'
  const live = me.status === 'active'
  const suspended = me.status === 'suspended'
  const foundingWanted = new URLSearchParams(window.location.search).get('plan') === 'founding'
  const foundingApplicant = me.subscription_tier === 'founding'

  const checkout = async (tier) => {
    try { window.location.assign((await attorneyApi.checkout(tier)).checkout_url) }
    catch (err) { setMsg(err.message || 'Subscriptions are being set up — check back soon.') }
  }
  const applyBoard = async () => {
    try { setMe(await attorneyApi.applyBoard()); setMsg('Review Board application submitted.') }
    catch (err) { setMsg(err.message || 'Could not apply') }
  }

  // SUSPENDED
  if (suspended) {
    return <Shell status={me.status} onSignOut={signOut} msg={msg}>
      <Card title="Your profile is suspended">
        <p className="text-sm text-slate-600">Please contact support to restore your listing.</p>
      </Card>
    </Shell>
  }

  // NOT VERIFIED — admin marked the bar registration as not verified.
  if (me.status === 'rejected') {
    return <Shell status={me.status} onSignOut={signOut}>
      <Card title="We couldn't verify your registration">
        <p className="text-sm text-slate-600">We weren't able to verify your Colorado bar registration, so your profile isn't published. If you believe this is a mistake — or your bar details have changed — contact us and we'll take another look.</p>
        <a href="mailto:support@coloradoleasecheck.com" className="mt-4 inline-block text-sm font-semibold text-emerald-700 hover:underline">support@coloradoleasecheck.com</a>
      </Card>
    </Shell>
  }

  // FOUNDING — application submitted, awaiting Tez's review. No Stripe.
  if (foundingApplicant && !active) {
    return <Shell status={me.status} onSignOut={signOut} msg={msg}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Your founding application is in</h1>
        <p className="mt-1 text-sm text-slate-500">Thank you for applying for Free Founding Membership. Every application is personally reviewed.</p>
      </div>
      <Card>
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-amber-100 text-lg text-amber-700">⏳</span>
          <div>
            <p className="text-sm font-semibold text-slate-800">Under review</p>
            <p className="mt-1 text-sm text-slate-600">We're confirming your Colorado bar registration and reviewing your profile. Nothing more to do — we'll email you at {me.email} with our decision.</p>
          </div>
        </div>
      </Card>
    </Shell>
  }

  // FOUNDING — build the profile and submit the application (no plan step).
  if (foundingWanted && !foundingApplicant && !active) {
    const submitFounding = async () => {
      try { setMe(await attorneyApi.foundingApply()); setMsg('') }
      catch (err) { setMsg(err.message || 'Could not submit your application') }
    }
    return <Shell status={me.status} onSignOut={signOut} msg={msg} step={2}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Apply for Free Founding Membership</h1>
        <p className="mt-1 text-sm text-slate-500">Complete your profile — including your Colorado bar number — and submit. We personally review every founding application and email you the decision.</p>
      </div>
      <Card>
        <ProfileForm me={me} setMe={setMe} emphasizeBar submitLabel="Submit application" onMsg={setMsg} onConfirmed={submitFounding} />
      </Card>
    </Shell>
  }

  // STEP 2 — CHOOSE A PLAN (not subscribed)
  if (!active) {
    return <Shell status={me.status} onSignOut={signOut} msg={msg} step={2} width="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Choose your plan, {me.name?.split(' ')[0] || 'Counsel'}</h1>
        <p className="mt-1 text-sm text-slate-500">One flat monthly fee to be listed and receive referrals. Cancel anytime.</p>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        {Object.entries(plans).map(([key, p]) => {
          const featured = key === 'featured'
          return (
            <div key={key} className={`flex flex-col rounded-2xl border bg-white p-6 shadow-sm ${featured ? 'border-emerald-500 ring-1 ring-emerald-200' : 'border-slate-200'}`}>
              {featured && <span className="mb-2 inline-block w-fit rounded-full bg-emerald-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">Most visible</span>}
              <div className="flex items-baseline justify-between">
                <h3 className="text-lg font-bold text-slate-900">{p.name}</h3>
                <span className="text-slate-700"><span className="text-2xl font-bold">${p.price_usd}</span>/{p.interval}</span>
              </div>
              <ul className="mt-4 flex-1 space-y-2 text-sm text-slate-600">
                {p.features.map((f) => <li key={f} className="flex gap-2"><span className="text-emerald-500">✓</span>{f}</li>)}
              </ul>
              <button onClick={() => checkout(key)}
                className={`mt-6 w-full rounded-xl py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 ${featured ? 'bg-gradient-to-r from-emerald-600 to-teal-600' : 'bg-slate-900 hover:bg-slate-800'}`}>
                Choose {p.name}
              </button>
            </div>
          )
        })}
      </div>
      <p className="mt-5 text-center text-xs text-slate-400">Secure checkout via Stripe. You can switch plans anytime.</p>
    </Shell>
  }

  // PENDING VERIFICATION — profile submitted, awaiting the admin bar check.
  if (me.status === 'pending') {
    return <Shell status={me.status} onSignOut={signOut} msg={msg} step={4}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">We're verifying your registration</h1>
        <p className="mt-1 text-sm text-slate-500">Your profile is submitted. We're confirming your Colorado bar registration — your listing goes live as soon as you're verified.</p>
      </div>
      <Card>
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-amber-100 text-lg text-amber-700">⏳</span>
          <div>
            <p className="text-sm font-semibold text-slate-800">Pending verification</p>
            <p className="mt-1 text-sm text-slate-600">Nothing more to do right now. You can close this page — we'll email you at {me.email} the moment your listing is live.</p>
          </div>
        </div>
      </Card>
    </Shell>
  }

  // STEP 3 — COMPLETE PROFILE (subscribed, still a draft)
  if (!live) {
    return <Shell status={me.status} onSignOut={signOut} msg={msg} step={3}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">One last step to go live</h1>
        <p className="mt-1 text-sm text-slate-500">Add your Colorado bar number and profile details. When you submit, we verify your bar registration with Colorado — then your profile goes live to renters.</p>
      </div>
      <Card>
        <ProfileForm me={me} setMe={setMe} emphasizeBar submitLabel="Submit for verification" onMsg={setMsg} />
      </Card>
    </Shell>
  }

  // LIVE DASHBOARD
  return <Shell status={me.status} onSignOut={signOut} msg={msg} width="max-w-3xl">
    {/* Top row — confirmation + Review Board, side by side */}
    <div className="grid gap-6 md:grid-cols-2">
      <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-6 sm:p-7">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-emerald-600 text-lg text-white">✓</span>
          <div>
            <h1 className="text-lg font-bold tracking-tight">You're live in the directory</h1>
            <p className="mt-1 text-sm text-slate-600">Thanks for joining LeaseCheck. Renters can now find and contact you — we'll email you the moment a referral comes in.</p>
            <p className="mt-3 text-sm text-slate-600">Subscription: <strong className="capitalize text-slate-800">{me.subscription_tier}</strong> · {me.subscription_status}</p>
          </div>
        </div>
        {PUBLIC_DIRECTORY_URL && (
          <button onClick={() => openPublicDirectory(me.id)}
            className="mt-4 inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-2.5 text-sm font-bold text-white shadow transition hover:-translate-y-0.5">
            View your listing
          </button>
        )}
      </div>

      {/* Review Board — the next opportunity, now that you're live */}
      <Card title="Review Board" desc="A small optional advisory group — 5 seats, selected from members.">
        {seats && seats.seats_remaining > 0 && (
          <p className="mb-4 inline-block rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800">{seats.seats_remaining} board {seats.seats_remaining === 1 ? 'seat' : 'seats'} open</p>
        )}
        {me.board_status === 'member'
          ? <BoardBadge tier={me.board_tier} member />
          : me.board_status === 'applied'
            ? <p className="text-sm font-medium text-amber-700">Application submitted — pending review.</p>
            : me.board_status === 'declined'
            ? <p className="text-sm text-slate-600">We weren't able to offer you a seat this time. Questions? <a href="mailto:support@coloradoleasecheck.com" className="font-semibold text-emerald-700 hover:underline">support@coloradoleasecheck.com</a></p>
            : <button onClick={applyBoard} disabled={seats && seats.seats_remaining <= 0}
                className="rounded-xl border border-emerald-600 px-5 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-50">
                {seats && seats.seats_remaining <= 0 ? 'Board is full' : 'Apply to the Review Board'}
              </button>}
      </Card>
    </div>

    {/* Your listing */}
    <div className="mt-6">
      <Card title="Your listing" desc="This is what renters see. Edit anytime — changes are live immediately.">
        <ProfileForm me={me} setMe={setMe} submitLabel="Save changes" onMsg={setMsg} />
      </Card>
    </div>
  </Shell>
}

export default Portal
